import { isFeatureEnabled } from "../../externalApi/VelocityApi";
import { Measures } from "../../../telemetry/Measures";
import { dashboard } from "../../../telemetry/TraceLogging";
import { addNewAccountAsync, getHostAuthenticatorApi, getHostAuthenticatorApiV5, getIsAccountAvailable, getSignInStatus, setSignInStatus } from "../../externalApi/AuthenticatorApi";
import { requestRefresh } from "../../externalApi/DashboardHostApi";
import { WidgetForceRefreshUnify1SCallVerb } from "../apis/api.interfaces";
import { refreshFeedsContentAsync } from "../../externalApi/FeedsApi";
export class AuthenticatorApi {
    constructor() {
        this._eventListeners = new Array();
    }
    async initialize() {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            await Promise.all([
                authenticator.addEventListener("identitystatuschanged", () => {
                    this.fireEvent("identitystatuschanged");
                }),
                authenticator.addEventListener("reauthrequiredstatuschanged", () => {
                    this.fireEvent("reauthrequiredstatuschanged");
                }),
            ]);
        }
    }
    // Returns an array of accountId strings for each account in WAM
    async getAccountIdsAsync() {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await authenticator.getAccountIds();
        }
        return new Array();
    }
    // Return the id of the logon account
    async getDefaultAccountIdAsync() {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await authenticator.getDefaultAccountId();
        }
        return "";
    }
    // Return the id of the first account that is not the same type (AAD/MSA) as the logon account
    async getSecondaryAccountIdAsync() {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await authenticator.getSecondaryAccountId();
        }
        return "";
    }
    // Is the account a MSA
    async isMSAAccountAsync(accountId) {
        const providerAuthority = await this.getAccountPropertyAsync(accountId, "ProviderAuthority");
        return providerAuthority.toLowerCase() === "consumers";
    }
    // Is the account an AAD
    async isAADAccountAsync(accountId) {
        const providerAuthority = await this.getAccountPropertyAsync(accountId, "ProviderAuthority");
        return providerAuthority.toLowerCase() === "organizations";
    }
    // Returns a "data:image/png;base64," iamge stream for the user avatar
    async getAccountPictureAsync(accountId) {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await new Promise(async (resolve, reject) => {
                try {
                    authenticator.getAccountPictureAsync(accountId, (imageString) => {
                        resolve(imageString);
                    });
                }
                catch (e) {
                    reject(e);
                }
            });
        }
        return "";
    }
    // Returns a property of an account such as:
    //  AccountId, UPN, DisplayName, OID, Authority, FirstName, LastName,  MUID, ProviderAuthority (i.e. "consumers"|"organizations")
    async getAccountPropertyAsync(accountId, name) {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await authenticator.getAccountProperty(accountId, name);
        }
        return "";
    }
    // Returns an authentication token and result code for an account with the specified requirements.
    async getAccountTokenAsync(accountId, clientId, scope, resource, apiVersion, requestSource) {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            return await new Promise(async (resolve, reject) => {
                authenticator.getAccountTokenAsync(accountId, clientId, scope, resource, apiVersion, requestSource, (token, errorResult, interactionRequired, errorMessage) => {
                    resolve({ token, errorResult, interactionRequired, errorMessage });
                });
            });
        }
        const token = "";
        const errorResult = 1;
        const errorMessage = "";
        const interactionRequired = false;
        return { token, errorResult, interactionRequired, errorMessage };
    }
    // Sets whether the app is signed in or not.
    async setSignInStatus(status) {
        const authenticator = await getHostAuthenticatorApi(2);
        if (authenticator) {
            await authenticator.setSignInStatus(status);
        }
    }
    // Returns whether app is in re-auth required state.
    async getReauthRequiredStatusAsync() {
        const authenticator = await getHostAuthenticatorApi(3);
        if (authenticator) {
            return await authenticator.getReauthRequiredStatus();
        }
        return false;
    }
    // Starts the "verify account" flow which allows interactive auth prompt to show for the primary token request.
    async verifyAccountAsync(entrypoint = "serverbanner") {
        const authenticator = await getHostAuthenticatorApi(3);
        if (authenticator) {
            Measures.logEvent(dashboard, "WebAppVerifyAccountAsync", 16777216 /* productAndServicePerformance */, {
                entrypoint: entrypoint,
            });
            authenticator.verifyAccountAsync(async () => {
                // Trigger a full reload which could fix the auth issue as a side effect.
                // Otherwise, interactive auth prompt will be shown.
                await requestRefresh();
            });
        }
    }
    // Sign in the user. If user is local account only, start the "add MSA" flow. If user has web account, start using the web account again.
    async signInUserAsync(entrypoint = "banner") {
        // With the V5 auth api, the sign in button owns making the token requests. It calls this to refresh the page after.
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            Measures.logEvent(dashboard, "WebAppSignInUserAsync", 16777216 /* productAndServicePerformance */, {
                entrypoint: entrypoint,
                isAccountAvailable: true,
            });
            // Don't care about status, verb or data. Just get refreshed content, then refresh the page.
            await refreshFeedsContentAsync(WidgetForceRefreshUnify1SCallVerb.refresh, "", async (status) => { await requestRefresh(); });
            return;
        }
        const isAccountAvailable = await getIsAccountAvailable();
        Measures.logEvent(dashboard, "WebAppSignInUserAsync", 16777216 /* productAndServicePerformance */, {
            entrypoint: entrypoint,
            isAccountAvailable: isAccountAvailable,
        });
        if (isAccountAvailable) {
            // User already has default web account, silently sign in.
            await setSignInStatus(1 /* SignedIn */);
            await requestRefresh();
        }
        else {
            // Add new MSA
            addNewAccountAsync();
        }
    }
    // Returns whether user is signed in.
    async isUserSignedInAsync() {
        return 2 /* SignedOut */ != (await getSignInStatus());
    }
    // Sign out the user. No-op if user is not signed-in.
    async signOutUserAsync(entrypoint = "serverbanner") {
        const isUserSignedIn = await this.isUserSignedInAsync();
        Measures.logEvent(dashboard, "WebAppSignOutUserAsync", 16777216 /* productAndServicePerformance */, {
            entrypoint: entrypoint,
            isUserSignedIn: isUserSignedIn,
        });
        if (isUserSignedIn) {
            await setSignInStatus(2 /* SignedOut */);
            // Force a refresh so that the page and all of its connections
            // to the host are discarded.
            await requestRefresh();
        }
    }
    async getSignInType(clientId) {
        const authenticator = await getHostAuthenticatorApiV5();
        if (authenticator) {
            return await authenticator.getSignInType(clientId);
        }
        return 0;
    }
    async getSignInTargetAccountId() {
        const authenticator = await getHostAuthenticatorApiV5();
        if (authenticator) {
            return await authenticator.getSignInTargetAccountId();
        }
        return "";
    }
    async getSignedInAccount(clientId) {
        const authenticator = await getHostAuthenticatorApiV5();
        if (authenticator) {
            return await authenticator.getSignedInAccount(clientId);
        }
        return "";
    }
    async signOutAccount(clientId) {
        const authenticator = await getHostAuthenticatorApiV5();
        if (authenticator) {
            await authenticator.signOutAccount(clientId);
            // Don't care about status, verb or data. Just get refreshed content, then refresh the page.
            await refreshFeedsContentAsync(WidgetForceRefreshUnify1SCallVerb.refresh, "", async (status) => { await requestRefresh(); });
        }
    }
    async getAccountPickedTokenAsync(msaClientId, aadClientId, msaScope, aadScope, msaResource, aadResource, apiVersion, requestSource) {
        const authenticator = await getHostAuthenticatorApiV5();
        if (authenticator) {
            return await new Promise(async (resolve, reject) => {
                authenticator.getAccountPickedTokenAsync(msaClientId, aadClientId, msaScope, aadScope, msaResource, aadResource, apiVersion, requestSource, (token, errorResult, interactionRequired, errorMessage) => {
                    resolve({ token, errorResult, interactionRequired, errorMessage });
                });
            });
        }
        const token = "";
        const errorResult = 1;
        const interactionRequired = false;
        const errorMessage = "";
        return { token, errorResult, interactionRequired, errorMessage };
    }
    addEventListener(listener) {
        this._eventListeners.push(listener);
    }
    removeEventListener(listener) {
        let index = this._eventListeners.indexOf(listener);
        if (index > -1) {
            this._eventListeners.splice(index, 1);
        }
    }
    fireEvent(eventName) {
        for (let index = 0; index < this._eventListeners.length; index++) {
            if (this._eventListeners[index]) {
                this._eventListeners[index](eventName);
            }
        }
    }
}
